import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { Button, Modal, SingleLineText, TypeInput, ValidationTextField } from '@bloomays-lib/ui.shared';
import { Logger } from '../../services/serviceLogger';

const logger = Logger('DeleteActionPopin');

type DeleteActionPopinProps = {
  opened: boolean;
  onClose: () => void;
  deleteConfirmLabel: string;
  confirmLabel: string;
  expectedValue: string;
  deleteAction: () => Promise<void>;
  /** Optional content rendered before the confirmation input (e.g. reason selector) */
  beforeConfirmContent?: React.ReactNode;
  /** When true, the delete button stays disabled even if the confirmation word matches */
  confirmExtraDisabled?: boolean;
};

const DeleteActionPopin = ({
  opened,
  onClose,
  confirmLabel,
  deleteAction,
  expectedValue,
  deleteConfirmLabel,
  beforeConfirmContent,
  confirmExtraDisabled = false,
}: DeleteActionPopinProps) => {
  const { t } = useTranslation(['random']);
  const [value, setValue] = useState('');
  const [disableButton, setDisableButton] = useState(true);

  const handleOnClick = async () => {
    setDisableButton(true);
    try {
      await deleteAction();
    } catch (err) {
      setDisableButton(false);
      logger.error('Erreur lors du delete', { err }, err as Error);
    }
  };

  const isDeleteDisabled = disableButton || confirmExtraDisabled;

  return (
    <Modal
      additionnalCSS={{
        overflowY: 'auto',
        width: '100%',
        maxWidth: 'min(440px, calc(100vw - 32px))',
        padding: '24px',
        border: 'none',
        borderRadius: '16px',
      }}
      open={opened}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <SingleLineText variant={'body2Medium'} text={deleteConfirmLabel} />
        {beforeConfirmContent}
        <ValidationTextField
          type={TypeInput.email}
          label={confirmLabel}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value.trim();
            setValue(inputValue);
            setDisableButton(inputValue !== expectedValue);
          }}
          value={value}
          size={'small'}
          placeholder={confirmLabel}
          error={false}
        />
        <Button onClick={handleOnClick} disable={isDeleteDisabled} textButton={t('delete', { ns: 'random' })} />
      </Box>
    </Modal>
  );
};

export default DeleteActionPopin;
